import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";

import useReceiptUploadConfig from "../components/UploadReceiptDialog/useReceiptUploadConfig";

import {
  deleteReceipt,
  isRetryableUploadError,
  uploadReceiptFile,
} from "./uploadRequest";
import {
  INITIAL_UPLOAD_SESSION_STATE,
  UPLOAD_CONCURRENCY,
  selectCounters,
  uploadSessionReducer,
} from "./uploadSessionReducer";
import { useInFlightReceiptStatuses } from "./useInFlightReceiptStatuses";

import type { UploadItem, UploadSessionCounters } from "./uploadSessionReducer";

export type UploadSessionContextValue = {
  items: UploadItem[];
  counters: UploadSessionCounters;
  dismissed: boolean;
  recentlyResolvedIds: string[];
  isReady: boolean;
  add: (files: File[]) => void;
  cancel: (id: string) => void;
  remove: (id: string) => void;
  retry: (id: string) => void;
  abortUnsent: () => void;
  dismissProgress: () => void;
};

export const UploadSessionContext =
  createContext<UploadSessionContextValue | null>(null);

type UploadSessionProviderProps = {
  children: React.ReactNode;
  // The config endpoint 403s for users without receipts, so pages gate the fetch.
  isEnabled?: boolean;
};

export const UploadSessionProvider = ({
  children,
  isEnabled = true,
}: UploadSessionProviderProps) => {
  const [state, dispatch] = useReducer(
    uploadSessionReducer,
    INITIAL_UPLOAD_SESSION_STATE
  );
  const { status: configStatus, config } = useReceiptUploadConfig(isEnabled);
  const controllersRef = useRef(new Map<string, AbortController>());
  // Uploads cancelled after the server already accepted them get deleted instead of kept.
  const cancelledIdsRef = useRef(new Set<string>());
  const itemsRef = useRef(state.items);

  useEffect(() => {
    itemsRef.current = state.items;
  }, [state.items]);

  useEffect(() => {
    const controllers = controllersRef.current;
    const cancelledIds = cancelledIdsRef.current;
    const uploading = state.items.filter(
      (item) => item.status === "uploading"
    ).length;
    const freeSlots = UPLOAD_CONCURRENCY - uploading;
    if (freeSlots <= 0) return;
    state.items
      .filter((item) => item.status === "queued" && !controllers.has(item.id))
      .slice(0, freeSlots)
      .forEach((item) => {
        const controller = new AbortController();
        controllers.set(item.id, controller);
        dispatch({ type: "start", id: item.id });
        uploadReceiptFile(item.file, { signal: controller.signal })
          .then((result) => {
            const wasCancelled = cancelledIds.delete(item.id);
            if (result.kind === "accepted") {
              if (wasCancelled) {
                deleteReceipt(result.receiptId);
                return;
              }
              dispatch({
                type: "accepted",
                id: item.id,
                receiptId: result.receiptId,
              });
            } else if (result.kind === "throttled") {
              dispatch({
                type: "throttled",
                id: item.id,
                retryAfterSeconds: result.retryAfterSeconds,
              });
            } else if (result.kind === "failed") {
              dispatch({
                type: "failed",
                id: item.id,
                errorCode: result.errorCode,
                retryable: isRetryableUploadError(result.errorCode),
              });
            }
          })
          .finally(() => {
            controllers.delete(item.id);
          });
      });
  }, [state.items]);

  useEffect(() => {
    const controllers = controllersRef.current;
    return () => {
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
    };
  }, []);

  const scanningIds = useMemo(
    () =>
      state.items
        .flatMap((item) =>
          item.status === "scanning" && item.receiptId ? [item.receiptId] : []
        )
        .sort(),
    [state.items]
  );

  const onResolved = useCallback(
    (receiptId: string, receiptStatus: string) =>
      dispatch({ type: "resolved", receiptId, receiptStatus }),
    []
  );

  const onStalled = useCallback(() => dispatch({ type: "stalled" }), []);

  useInFlightReceiptStatuses({
    receiptIds: scanningIds,
    activityCount: state.activityCount,
    onResolved,
    onStalled,
  });

  const abortItem = useCallback((id: string) => {
    controllersRef.current.get(id)?.abort();
    controllersRef.current.delete(id);
  }, []);

  const add = useCallback(
    (files: File[]) => {
      if (!config) return;
      dispatch({ type: "add", files, config });
    },
    [config]
  );

  const cancel = useCallback(
    (id: string) => {
      const item = itemsRef.current.find((candidate) => candidate.id === id);
      if (!item) return;
      if (item.status === "scanning" && item.receiptId) {
        deleteReceipt(item.receiptId);
      } else if (controllersRef.current.has(id)) {
        cancelledIdsRef.current.add(id);
        abortItem(id);
      }
      dispatch({ type: "cancel", id });
    },
    [abortItem]
  );

  const remove = useCallback(
    (id: string) => dispatch({ type: "remove", id }),
    []
  );

  const retry = useCallback(
    (id: string) => dispatch({ type: "retry", id }),
    []
  );

  const abortUnsent = useCallback(() => {
    controllersRef.current.forEach((controller) => controller.abort());
    controllersRef.current.clear();
    dispatch({ type: "abortUnsent" });
  }, []);

  const dismissProgress = useCallback(
    () => dispatch({ type: "dismissProgress" }),
    []
  );

  const counters = useMemo(() => selectCounters(state), [state]);

  const value = useMemo<UploadSessionContextValue>(
    () => ({
      items: state.items,
      counters,
      dismissed: state.dismissed,
      recentlyResolvedIds: state.recentlyResolvedIds,
      isReady: configStatus === "ready" && config !== null,
      add,
      cancel,
      remove,
      retry,
      abortUnsent,
      dismissProgress,
    }),
    [
      state.items,
      state.dismissed,
      state.recentlyResolvedIds,
      counters,
      configStatus,
      config,
      add,
      cancel,
      remove,
      retry,
      abortUnsent,
      dismissProgress,
    ]
  );

  return (
    <UploadSessionContext.Provider value={value}>
      {children}
    </UploadSessionContext.Provider>
  );
};
